'use client';

import { useId } from 'react';
import { Box, Flex, Text, Image } from '@skin-seoul/design-system';
import { useGetShippingPolicy, formatCurrency, useRegionCurrency, getMediaOrigin } from '@skin-seoul/service';

// Image path constants
const LOYALTY_IMAGE_PATHS = {
  SKIN_SEOUL_LOGO: '/loyalty/skin-seoul-logo.png',
} as const;

interface BenefitItem {
  icon: React.ReactNode;
  title: string;
  description: string;
}

const TruckIcon = () => {
  const gradientId = useId();

  return (
    <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M16.3359 20.9993V6.99935C16.3359 6.38051 16.0901 5.78702 15.6525 5.34943C15.2149 4.91185 14.6214 4.66602 14.0026 4.66602H4.66927C4.05043 4.66602 3.45694 4.91185 3.01935 5.34943C2.58177 5.78702 2.33594 6.38051 2.33594 6.99935V19.8327C2.33594 20.1421 2.45885 20.4388 2.67765 20.6576C2.89644 20.8764 3.19318 20.9993 3.5026 20.9993H5.83594M5.83594 20.9993C5.83594 22.288 6.88061 23.3327 8.16927 23.3327C9.45794 23.3327 10.5026 22.288 10.5026 20.9993M5.83594 20.9993C5.83594 19.7107 6.88061 18.666 8.16927 18.666C9.45794 18.666 10.5026 19.7107 10.5026 20.9993M17.5026 20.9993H10.5026M17.5026 20.9993C17.5026 22.288 18.5473 23.3327 19.8359 23.3327C21.1246 23.3327 22.1693 22.288 22.1693 20.9993M17.5026 20.9993C17.5026 19.7107 18.5473 18.666 19.8359 18.666C21.1246 18.666 22.1693 19.7107 22.1693 20.9993M22.1693 20.9993H24.5026C24.812 20.9993 25.1088 20.8764 25.3276 20.6576C25.5464 20.4388 25.6693 20.1421 25.6693 19.8327V15.5743C25.6688 15.3096 25.5783 15.0529 25.4126 14.8463L21.3526 9.77135C21.2435 9.63471 21.1051 9.52434 20.9475 9.44841C20.79 9.37248 20.6175 9.33293 20.4426 9.33268H16.3359"
        stroke={`url(#${gradientId})`}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <defs>
        <linearGradient
          id={gradientId}
          x1="14.0026"
          y1="4.66602"
          x2="14.0026"
          y2="23.3327"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="white" />
          <stop offset="1" stopColor="#999999" />
        </linearGradient>
      </defs>
    </svg>
  );
};

const GiftIcon = () => {
  const gradientId = useId();

  return (
    <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M14 9.33384V24.5005M14 9.33384C13.578 7.59481 12.8514 6.10808 11.9149 5.06753C10.9784 4.02698 9.87547 3.4809 8.75 3.50051C7.97645 3.50051 7.23459 3.8078 6.68761 4.35478C6.14062 4.90176 5.83333 5.64363 5.83333 6.41718C5.83333 7.19072 6.14062 7.93259 6.68761 8.47957C7.23459 9.02655 7.97645 9.33384 8.75 9.33384M14 9.33384C14.422 7.59481 15.1486 6.10808 16.0851 5.06753C17.0216 4.02698 18.1245 3.4809 19.25 3.50051C20.0235 3.50051 20.7654 3.8078 21.3124 4.35478C21.8594 4.90176 22.1667 5.64363 22.1667 6.41718C22.1667 7.19072 21.8594 7.93259 21.3124 8.47957C20.7654 9.02655 20.0235 9.33384 19.25 9.33384M22.1667 14.0005V22.1672C22.1667 22.786 21.9208 23.3795 21.4832 23.8171C21.0457 24.2547 20.4522 24.5005 19.8333 24.5005H8.16667C7.54783 24.5005 6.95434 24.2547 6.51675 23.8171C6.07917 23.3795 5.83333 22.786 5.83333 22.1672V14.0005M4.66667 9.33384H23.3333C23.9777 9.33384 24.5 9.85618 24.5 10.5005V12.8338C24.5 13.4782 23.9777 14.0005 23.3333 14.0005H4.66667C4.02233 14.0005 3.5 13.4782 3.5 12.8338V10.5005C3.5 9.85618 4.02233 9.33384 4.66667 9.33384Z"
        stroke={`url(#${gradientId})`}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <defs>
        <linearGradient id={gradientId} x1="14" y1="3.5" x2="14" y2="24.5005" gradientUnits="userSpaceOnUse">
          <stop stopColor="white" />
          <stop offset="1" stopColor="#999999" />
        </linearGradient>
      </defs>
    </svg>
  );
};

const PercentIcon = () => {
  const gradientId = useId();

  return (
    <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M22.1641 5.83268L5.83073 22.166M10.4974 7.58268C10.4974 9.19351 9.19156 10.4993 7.58073 10.4993C5.9699 10.4993 4.66406 9.19351 4.66406 7.58268C4.66406 5.97185 5.9699 4.66602 7.58073 4.66602C9.19156 4.66602 10.4974 5.97185 10.4974 7.58268ZM23.3307 20.416C23.3307 22.0268 22.0249 23.3327 20.4141 23.3327C18.8032 23.3327 17.4974 22.0268 17.4974 20.416C17.4974 18.8052 18.8032 17.4993 20.4141 17.4993C22.0249 17.4993 23.3307 18.8052 23.3307 20.416Z"
        stroke={`url(#${gradientId})`}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <defs>
        <linearGradient
          id={gradientId}
          x1="13.9974"
          y1="4.66602"
          x2="13.9974"
          y2="23.3327"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="white" />
          <stop offset="1" stopColor="#999999" />
        </linearGradient>
      </defs>
    </svg>
  );
};

interface SkinSocietyBenefitsProps {
  title?: string;
  showSigninComment?: boolean;
}

export const SkinSocietyBenefits = ({
  title = 'SkinSociety Benefits',
  showSigninComment = false,
}: SkinSocietyBenefitsProps) => {
  const { country } = useRegionCurrency();
  const { shippingPolicy } = useGetShippingPolicy(country);
  const freeShippingDescription = `On all orders over ${shippingPolicy ? formatCurrency(shippingPolicy.currencyCode, shippingPolicy.freeShippingThreshold) : ''}`;

  const benefits: BenefitItem[] = [
    {
      icon: <TruckIcon />,
      title: 'FREE SHIPPING',
      description: freeShippingDescription,
    },
    {
      icon: <GiftIcon />,
      title: 'BIRTHDAY GIFT',
      description: 'Special surprise on your birthday',
    },
    {
      icon: <PercentIcon />,
      title: 'DISCOUNTS',
      description: 'Member-only sales & early access',
    },
  ];

  return (
    <Box position="relative" width="100%" borderRadius="12px" overflow="hidden" bg="primary.900" p={4}>
      {/* Content */}
      <Flex direction="column" gap={8} position="relative" zIndex={1}>
        {/* Header */}
        <Flex direction="column" gap={3} alignItems="center">
          {/* Logo */}

          {showSigninComment ? (
            <Flex direction="column" gap={3} alignItems="center" width="100%" mt={2}>
              <Text
                textStyle="heading.h2.mobile"
                fontWeight="bold"
                color="white"
                textAlign="center"
                letterSpacing="-0.02em"
              >
                Sign In to{' '}
                <Text as="span" color="primary.300">
                  Unlock Your SkinSociety Rewards
                </Text>
              </Text>
              <Text textStyle="paragraph.small" fontWeight="normal" color="#E8E8EB" textAlign="center">
                earn points, redeem rewards, and enjoy special offers every time you shop.
              </Text>
            </Flex>
          ) : (
            <Box width="33px" height="30px" display="flex" alignItems="center" justifyContent="center">
              <Image
                src={`${getMediaOrigin()}${LOYALTY_IMAGE_PATHS.SKIN_SEOUL_LOGO}`}
                alt="Skin Seoul Logo"
                width={33}
                height={30}
              />
            </Box>
          )}
          {/* Title */}
          <Text
            textStyle={showSigninComment ? 'heading.h5.mobile' : 'heading.h2.mobile'}
            fontWeight="bold"
            color="primary.100"
            textAlign="center"
          >
            {title}
          </Text>

          {/* Badge */}
          <Box
            bgGradient="to-r"
            gradientFrom="#f1eaff"
            gradientTo="#ffffff"
            px={4}
            py={1}
            borderRadius="4px"
            width={showSigninComment ? 'auto' : '100%'}
            textAlign="center"
          >
            <Text textStyle="label.small" fontWeight="semibold" color="primary.600">
              EXCLUSIVELY MADE FOR YOU
            </Text>
          </Box>
        </Flex>

        {/* Benefits Section */}
        <Flex direction="column" gap={4}>
          {/* Section Divider */}
          <Flex alignItems="center" justifyContent="center" gap={3}>
            <Box flex="1" height="1px" bg="primary.200" opacity={0.3} />
            <Text textStyle="label.xsmall" fontWeight="semibold" color="primary.200">
              BENEFITS AT A GLANCE
            </Text>
            <Box flex="1" height="1px" bg="primary.200" opacity={0.3} />
          </Flex>

          {/* Mobile Layout - Below tablet breakpoint */}
          <Box hideFrom="tablet">
            <Flex direction="column" gap={3}>
              {/* Free Shipping - Full Width */}
              <Box borderRadius="12px" overflow="hidden" border="1px solid" borderColor="#50368A">
                <Flex direction="column">
                  {/* Badge Header */}
                  <Box
                    bgGradient="to-r"
                    gradientFrom="#1a0743"
                    gradientVia="#4f1ebb"
                    gradientTo="#1a0743"
                    py={2}
                    px={1}
                    textAlign="center"
                  >
                    <Text textStyle="label.xsmall" fontWeight="semibold" color="primary.100">
                      {benefits[0].title}
                    </Text>
                  </Box>

                  {/* Content */}
                  <Flex alignItems="center" justifyContent="center" gap={3} bg="primary.700" p={3}>
                    {/* Icon */}
                    <Box width="28px" height="28px" flexShrink={0} display="flex">
                      {benefits[0].icon}
                    </Box>

                    {/* Description */}
                    <Text textStyle="paragraph.xsmall" fontWeight="medium" color="white" textAlign="center">
                      {benefits[0].description}
                    </Text>
                  </Flex>
                </Flex>
              </Box>

              {/* Birthday & Discounts - Side by Side */}
              <Flex gap={3}>
                {benefits.slice(1).map((benefit, index) => (
                  <Box
                    key={index}
                    flex="1"
                    borderRadius="12px"
                    overflow="hidden"
                    border="1px solid"
                    borderColor="#50368A"
                  >
                    <Flex direction="column">
                      {/* Badge Header */}
                      <Box
                        bgGradient="to-r"
                        gradientFrom="#1a0743"
                        gradientVia="#4f1ebb"
                        gradientTo="#1a0743"
                        py={2}
                        px={1}
                        textAlign="center"
                      >
                        <Text textStyle="label.xsmall" fontWeight="semibold" color="primary.100">
                          {benefit.title}
                        </Text>
                      </Box>

                      {/* Content */}
                      <Flex
                        direction="column"
                        gap={3}
                        alignItems="center"
                        justifyContent="center"
                        bg="primary.700"
                        p={3}
                      >
                        {/* Icon */}
                        <Box width="28px" height="28px" flexShrink={0} display="flex">
                          {benefit.icon}
                        </Box>

                        {/* Description */}
                        <Text
                          textStyle="paragraph.xsmall"
                          fontWeight="medium"
                          color="white"
                          textAlign="center"
                          flex="1"
                        >
                          {benefit.description}
                        </Text>
                      </Flex>
                    </Flex>
                  </Box>
                ))}
              </Flex>
            </Flex>
          </Box>

          {/* Desktop Layout - Above tablet breakpoint */}
          <Box hideBelow="tablet">
            <Flex gap={3}>
              {benefits.map((benefit, index) => (
                <Box
                  key={index}
                  flex="1"
                  borderRadius="12px"
                  overflow="hidden"
                  border="1px solid"
                  borderColor="#50368A"
                >
                  <Flex direction="column">
                    {/* Badge Header */}
                    <Box
                      bgGradient="to-r"
                      gradientFrom="#1a0743"
                      gradientVia="#4f1ebb"
                      gradientTo="#1a0743"
                      py={2}
                      px={1}
                      textAlign="center"
                    >
                      <Text textStyle="label.xsmall" fontWeight="semibold" color="primary.100">
                        {benefit.title}
                      </Text>
                    </Box>

                    {/* Content */}
                    <Flex direction="column" gap={3} alignItems="center" justifyContent="center" bg="primary.700" p={3}>
                      {/* Icon */}
                      <Box width="28px" height="28px" flexShrink={0} display="flex">
                        {benefit.icon}
                      </Box>

                      {/* Description */}
                      <Text textStyle="paragraph.xsmall" fontWeight="medium" color="white" textAlign="center" flex="1">
                        {benefit.description}
                      </Text>
                    </Flex>
                  </Flex>
                </Box>
              ))}
            </Flex>
          </Box>
        </Flex>
      </Flex>
    </Box>
  );
};
